import { Suspense, useCallback, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { Icon } from "@zapier/design-system";
import { Button } from "@zapier/design-system-beta";
import {
  executeTemplateAndSaveAtom,
  isExecutingTemplateAtom,
  upgradeBannerPropsAsyncAtom,
  emitEventOnAIExecutionAtom,
  dismissFeedbackFormCommand,
  templateNameAtom,
  emitAiPredictionUserFeedbackEventAtom,
  templateFeedbackPredictionIdAtom,
  isAiPageAtom,
  numberOfUniqueTableNodesAtom,
  numberOfUniqueChatbotNodesAtom,
  numberOfUniqueInterfaceNodesAtom,
} from "@zapier/canvas-state";
import {
  aiPromptAtom,
  handleModifyPromptCommand,
  startOverCommand,
  tuningPromptsAtom,
} from "@zapier/streaming";

import { UpgradeBanner } from "../UpgradeBanner";
import { ProgressBanner } from "../ProgressBanner";
import { BannerWrapper } from "../BannerWrapper";
import { IMPLICIT_GOOD } from "@zapier/canvas-constants";

type Props = {
  handleModifyPrompt: () => void;
  handleStartOver: () => void;
  isStartOverButtonLoading: boolean;
};

function TemplateExecutionBanner({
  handleModifyPrompt,
  handleStartOver,
  isStartOverButtonLoading,
}: Props) {
  const executeTemplateAndSave = useSetAtom(executeTemplateAndSaveAtom);
  const emitEventOnAIExecution = useSetAtom(emitEventOnAIExecutionAtom);
  const emitImplicitGoodEvent = useSetAtom(
    emitAiPredictionUserFeedbackEventAtom,
  );
  const isTemplateExecuting = useAtomValue(isExecutingTemplateAtom);
  const prompt = useAtomValue(aiPromptAtom);
  const dismissFeedbackForm = useSetAtom(dismissFeedbackFormCommand);
  const setTuningPrompts = useSetAtom(tuningPromptsAtom);
  const prediction_id = useAtomValue(templateFeedbackPredictionIdAtom);

  const handleUseTemplate = useCallback(async () => {
    dismissFeedbackForm();
    emitEventOnAIExecution();

    emitImplicitGoodEvent({
      prediction_id,
      user_rating: IMPLICIT_GOOD,
      user_feedback: null,
    });
    setTuningPrompts([]);

    await executeTemplateAndSave({ prompt });
  }, [
    dismissFeedbackForm,
    emitEventOnAIExecution,
    emitImplicitGoodEvent,
    prediction_id,
    setTuningPrompts,
    executeTemplateAndSave,
    prompt,
  ]);

  const templateName = useAtomValue(templateNameAtom);
  const title = `You are previewing the generated system '${templateName}'`;
  const description =
    "Using this system will create the Zapier assets displayed below.";

  return (
    <BannerWrapper title={title} description={description}>
      <Button
        variant="tertiary"
        onPress={handleStartOver}
        isDisabled={isTemplateExecuting}
        isLoading={isStartOverButtonLoading}
      >
        Start over
      </Button>
      <Button
        variant="tertiary"
        onPress={handleModifyPrompt}
        isDisabled={isTemplateExecuting}
      >
        <Icon name="miscAI" size={30} />
        Modify prompt
      </Button>
      <Button isLoading={isTemplateExecuting} onPress={handleUseTemplate}>
        Use this system
      </Button>
    </BannerWrapper>
  );
}

function SuspendedBanner({
  handleModifyPrompt,
  handleStartOver,
  isStartOverButtonLoading,
}: Props) {
  const upgradeBannerProps = useAtomValue(upgradeBannerPropsAsyncAtom);

  if (upgradeBannerProps === null) {
    return (
      <TemplateExecutionBanner
        handleModifyPrompt={handleModifyPrompt}
        handleStartOver={handleStartOver}
        isStartOverButtonLoading={isStartOverButtonLoading}
      />
    );
  }

  return (
    <UpgradeBanner {...upgradeBannerProps} location="AI banner">
      <Button
        variant="tertiary"
        onPress={handleStartOver}
        isLoading={isStartOverButtonLoading}
      >
        Start over
      </Button>
      <Button variant="tertiary" onPress={handleModifyPrompt}>
        <Icon name="miscAI" size={30} />
        Modify prompt
      </Button>
    </UpgradeBanner>
  );
}

export function AiCopilotBanner() {
  const numberOfTableNodes = useAtomValue(numberOfUniqueTableNodesAtom);
  const numberOfInterfaceNodes = useAtomValue(numberOfUniqueInterfaceNodesAtom);
  const numberOfChatbotNodes = useAtomValue(numberOfUniqueChatbotNodesAtom);
  const shouldShowSuspendedBanner =
    numberOfTableNodes + numberOfInterfaceNodes + numberOfChatbotNodes > 0;
  const handleModifyPrompt = useSetAtom(handleModifyPromptCommand);
  const [isStartOverButtonLoading, setIsStartOverButtonLoading] =
    useState(false);

  const handleStartOver = useAtomCallback(
    useCallback(async (get, set) => {
      const isAiPage = get(isAiPageAtom);
      if (isAiPage) {
        /* Show loading state for AI page since it takes a while to create a new canvas
        from AI page which has no real canvas */
        setIsStartOverButtonLoading(true);
      }

      await set(startOverCommand);
    }, []),
  );

  if (shouldShowSuspendedBanner) {
    return (
      <Suspense fallback={<ProgressBanner />}>
        <SuspendedBanner
          handleModifyPrompt={handleModifyPrompt}
          handleStartOver={handleStartOver}
          isStartOverButtonLoading={isStartOverButtonLoading}
        />
      </Suspense>
    );
  }

  return (
    <TemplateExecutionBanner
      handleModifyPrompt={handleModifyPrompt}
      handleStartOver={handleStartOver}
      isStartOverButtonLoading={isStartOverButtonLoading}
    />
  );
}
